import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { dashboardAPI } from '@/api/dashboard';
import { showError } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LogOut, FolderKanban, Users, FileBarChart, ArrowRight, Settings, Database, Server, Landmark } from 'lucide-react';

// Dashboard Components
import ProjectHealthCard from '@/components/dashboard/ProjectHealthCard';
import FinancialOverview from '@/components/dashboard/FinancialOverview';
import ProjectsByManager from '@/components/dashboard/ProjectsByManager';
import LogisticsTracker from '@/components/dashboard/LogisticsTracker';

const DashboardPage = () => {
  const { user, logout, hasPageAccess } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    health: null,
    finances: null,
    resources: null,
    logistics: null,
  });

  // Проверяем доступ через матрицу
  const userRoleLower = user?.role?.toLowerCase();
  const canAccessProjects = hasPageAccess('/projects');
  const canAccessEmployees = hasPageAccess('/employees');
  const canAccessReports = hasPageAccess('/report-builder');
  const canAccessBitrix = hasPageAccess('/integrations/bitrix');
  const canAccessFieldRegistry = hasPageAccess('/admin/field-registry');
  const canAccessAgentDashboard = ['finance_admin', 'senior_manager', 'goszakup'].includes(user?.role);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [health, finances, resources, logistics] = await Promise.all([
        dashboardAPI.getProjectHealth(),
        dashboardAPI.getFinances(),
        dashboardAPI.getResources(),
        dashboardAPI.getLogistics(),
      ]);

      setDashboardData({ health, finances, resources, logistics });
    } catch (error) {
      showError(error, 'Ошибка загрузки данных дашборда');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Центр управления проектами</h1>
              <p className="text-sm text-gray-600 mt-1">
                Добро пожаловать, {user?.full_name} ({user?.role})
              </p>
            </div>
            <div className="flex items-center gap-3">
              {canAccessAgentDashboard && (
                <a href="/udp-dashboard.html" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="udp-dashboard-button"
                  >
                    <Landmark className="w-4 h-4" />
                    УДП
                  </Button>
                </a>
              )}
              {canAccessAgentDashboard && (
                <a href="/procurement-intel/" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="agent-dashboard-button"
                  >
                    <Server className="w-4 h-4" />
                    Procurement Intel
                  </Button>
                </a>
              )}
              {canAccessFieldRegistry && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/field-registry')}
                  className="flex items-center gap-2"
                  data-testid="field-registry-button"
                >
                  <Database className="w-4 h-4" />
                  Реестр полей
                </Button>
              )}
              {canAccessBitrix && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/integrations/bitrix')}
                  className="flex items-center gap-2"
                  data-testid="integrations-button"
                >
                  <Settings className="w-4 h-4" />
                  Интеграции
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex items-center gap-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Выход
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Access Cards - показываются по матрице доступа */}
        {(canAccessProjects || canAccessEmployees || canAccessReports) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {canAccessProjects && (
              <Card
                className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-indigo-500"
                onClick={() => navigate('/projects')}
                data-testid="quick-access-projects"
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <FolderKanban className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Все проекты</h3>
                      <p className="text-sm text-gray-500">Портфель проектов</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </CardContent>
              </Card>
            )}

            {canAccessReports && (
              <Card
                className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-emerald-500"
                onClick={() => navigate('/report-builder')}
                data-testid="quick-access-reports"
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <FileBarChart className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Конструктор отчётов</h3>
                      <p className="text-sm text-gray-500">Аналитика и экспорт</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </CardContent>
              </Card>
            )}

            {canAccessEmployees && (
              <Card
                className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-500"
                onClick={() => navigate('/employees')}
                data-testid="quick-access-employees"
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Сотрудники</h3>
                      <p className="text-sm text-gray-500">Управление командой</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Top Row: Health and Finances */}
          <ProjectHealthCard healthData={dashboardData.health} />
          <FinancialOverview financialData={dashboardData.finances} />

          {/* Bottom Row: Team and Logistics */}
          <ProjectsByManager resourceData={dashboardData.resources} />
          <LogisticsTracker logisticsData={dashboardData.logistics} />
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
